import { useEffect, useRef, type CSSProperties, type JSX, type ReactNode } from 'react';

/**
 * Diocese Financial Story — the public landing page for the Quota Model.
 *
 * A scroll-driven editorial "story" explaining the diocese's financial position
 * ahead of Synod. Ported faithfully from the design handoff (see this package's
 * README.md and `Diocese Financial Story.dc.html`): exact copy, colours, type,
 * spacing and Apple-style motion are all intentional.
 *
 * Deliberately a different register from the rest of the app (editorial serif/mono
 * vs the tool's Montserrat). All styling is inline and self-contained so nothing
 * leaks into the surrounding portal; it renders OUTSIDE the portal Shell because
 * the full-bleed hero conflicts with the PortalHeader.
 *
 * Core guarantee (from the spec): everything is visible by default (opacity 1).
 * Motion is layered on top via a single rAF loop and can never hide content; under
 * `prefers-reduced-motion` (or the reduceMotion prop) it renders fully static.
 */

type Palette = 'Claret & Gold' | 'Penitential Purple' | 'Forest & Brass';

const PALETTES: Record<
  Palette,
  { accent: string; accentSoft: string; gold: string; darkBg: string }
> = {
  'Claret & Gold': { accent: '#6e1f25', accentSoft: '#8f343a', gold: '#a9803f', darkBg: '#1c1813' },
  'Penitential Purple': {
    accent: '#4a2a5e',
    accentSoft: '#5f3a76',
    gold: '#9a7b3e',
    darkBg: '#1a1422',
  },
  'Forest & Brass': {
    accent: '#264a37',
    accentSoft: '#33614a',
    gold: '#a9803f',
    darkBg: '#141a16',
  },
};

const CREST = '/assets/cca_icon_reverse_RGB.png';
const EASE = 'cubic-bezier(.2,.7,.2,1)';
const REVEAL_TRANSITION = `opacity .9s ${EASE},transform .9s ${EASE}`;

// Scoped <style>: keyframes + print rules + landscape @page. Lives in the DOM only
// while the story is mounted, so the @page rule can't bleed into the tool's print.
const SCOPED_CSS = `
@keyframes cca-scrollcue{0%,100%{transform:translateY(0);opacity:.45}50%{transform:translateY(7px);opacity:1}}
@media print{
  /* Full-bleed landscape slide deck: one section per page, each section sized to
     exactly one page and its content centred + scaled to fit (verified via headless
     Chrome — 7 sections → 7 pages, nothing clipped or leaking). @page margin:0 so
     the dark sections bleed to the edge; spacing is internal padding. body margin
     reset kills a phantom trailing blank page. */
  @page{size:A4 landscape;margin:0}
  html,body{margin:0 !important;padding:0 !important}
  .cca-story *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important}
  .cca-story *,.cca-story *::before,.cca-story *::after{animation:none !important;transition:none !important}
  .cca-story [data-reveal],.cca-story [data-parallax],.cca-story [data-hero]{transform:none !important;opacity:1 !important}
  .cca-story [data-noprint]{display:none !important}
  .cca-story section{height:100vh !important;min-height:0 !important;box-sizing:border-box !important;display:flex !important;flex-direction:column !important;justify-content:center !important;padding:12mm 16mm !important;overflow:hidden !important;break-before:page !important;break-inside:avoid !important;break-after:auto !important}
  .cca-story section:first-of-type{break-before:avoid !important}
  .cca-story section > *{zoom:0.62 !important}
}`;

export function FinancialStory({
  onSignIn,
  palette = 'Claret & Gold',
  reduceMotion = false,
}: {
  onSignIn?: () => void;
  palette?: Palette;
  reduceMotion?: boolean;
}): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const progRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const reduce =
      reduceMotion || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

    const fmt = (n: number): string => Math.round(n).toLocaleString('en-NZ');
    const finalText = (el: HTMLElement): string =>
      (el.dataset.prefix ?? '') + fmt(Number(el.dataset.count)) + (el.dataset.suffix ?? '');

    // ── Data viz entrance: bars grow, counters tick up ──────────────────────
    const counters: number[] = [];
    const items = Array.from(root.querySelectorAll<HTMLElement>('[data-fill],[data-count]'));
    const still = reduce || document.hidden;
    items.forEach((el) => {
      if (el.hasAttribute('data-fill')) {
        if (still) {
          el.style.width = `${el.dataset.fill}%`;
          return;
        }
        const tr = el.style.transition;
        el.style.transition = 'none';
        el.style.width = `${el.dataset.start ?? '0'}%`;
        void el.offsetWidth; // force reflow so the start width is committed
        el.style.transition = tr || `width 1.2s ${EASE}`;
        requestAnimationFrame(() => {
          el.style.width = `${el.dataset.fill}%`;
        });
      }
      if (el.hasAttribute('data-count')) {
        if (still) {
          el.textContent = finalText(el);
          return;
        }
        const target = Number(el.dataset.count);
        const pre = el.dataset.prefix ?? '';
        const suf = el.dataset.suffix ?? '';
        el.textContent = `${pre}0${suf}`;
        const dur = 1500;
        const start = performance.now();
        const ease = (t: number): number => 1 - Math.pow(1 - t, 3);
        const step = (now: number): void => {
          const t = Math.min(1, (now - start) / dur);
          el.textContent = pre + fmt(target * ease(t)) + suf;
          if (t < 1) counters.push(requestAnimationFrame(step));
        };
        counters.push(requestAnimationFrame(step));
      }
    });

    // ── Scroll progress bar ─────────────────────────────────────────────────
    const onScroll = (): void => {
      const d = document.scrollingElement ?? document.documentElement;
      const max = d.scrollHeight - d.clientHeight || 1;
      const p = Math.min(1, Math.max(0, d.scrollTop / max));
      if (progRef.current) progRef.current.style.width = `${p * 100}%`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    // ── Apple-style scroll motion (transform only; never hides content) ──────
    const hero = root.querySelector<HTMLElement>('[data-hero]');
    const reveals = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]')).map((el) => {
      el.style.transition = 'none';
      return { el, cur: 0 };
    });
    const px = Array.from(root.querySelectorAll<HTMLElement>('[data-parallax]')).map((el) => ({
      el,
      speed: parseFloat(el.dataset.parallax ?? '') || 0.06,
      cur: 0,
    }));

    let raf = 0;
    if (!reduce) {
      const ease = (t: number): number => 1 - Math.pow(1 - t, 3);
      const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);
      let started = false;
      let hp: number | null = null;

      const tick = (): void => {
        const vh = window.innerHeight || 1;

        const startY = vh * 0.96;
        const endY = vh * 0.6;
        for (const o of reveals) {
          const top = o.el.getBoundingClientRect().top;
          const p = clamp((startY - top) / (startY - endY), 0, 1);
          const target = (1 - ease(p)) * 34;
          o.cur = started ? o.cur + (target - o.cur) * 0.14 : target;
          o.el.style.transform = o.cur > 0.1 ? `translate3d(0,${o.cur.toFixed(2)}px,0)` : 'none';
        }

        for (const o of px) {
          const r = o.el.getBoundingClientRect();
          const t = (r.top + r.height / 2 - vh / 2) * -o.speed;
          o.cur = started ? o.cur + (t - o.cur) * 0.1 : t;
          o.el.style.transform = `translate3d(0,${o.cur.toFixed(2)}px,0)`;
        }

        if (hero) {
          const y =
            (document.scrollingElement ?? document.documentElement).scrollTop ||
            window.scrollY ||
            0;
          const tp = clamp(y / (vh * 0.85), 0, 1);
          hp = hp == null || !started ? tp : hp + (tp - hp) * 0.12;
          hero.style.transform = `translate3d(0,${(hp * -80).toFixed(1)}px,0) scale(${(
            1 +
            hp * 0.05
          ).toFixed(3)})`;
          hero.style.opacity = clamp(1 - hp * 1.2, 0, 1).toFixed(3);
        }

        started = true;
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      counters.forEach(cancelAnimationFrame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [reduceMotion, palette]);

  const c = PALETTES[palette];
  const rootStyle: CSSProperties = {
    // Palette + fixed tokens, mirrored from the design source — except the two
    // warm greys, which the source set below WCAG 2.2 AA (SC 1.4.3, 4.5:1) for
    // the 11–14px mono labels they carry. Each is darkened (or, on the dark
    // sections, lightened) a measured step past the floor, keeping its warm hue:
    //   --muted          was #8a7e6c: 3.47:1 on --paper, 3.12:1 on --paper-2.
    //                    Now 5.14:1 on --paper, 4.63:1 on --paper-2.
    //   --muted-on-dark  was #8c7f66: 4.49:1 on the default dark ground.
    //                    Now 5.01:1 on #1c1813, 5.11:1 on #1a1422, 5.02:1 on
    //                    #141a16 (each palette's darkBg). Aimed at about 5:1,
    //                    not just past 4.5, because it carries 11px letter-
    //                    spaced mono, whose thin strokes antialias lighter.
    ['--paper' as string]: '#f4efe6',
    ['--paper-2' as string]: '#ece3d3',
    ['--ink' as string]: '#241e17',
    ['--ink-soft' as string]: '#4b4135',
    ['--muted' as string]: '#6d6355',
    ['--muted-on-dark' as string]: '#95876d',
    ['--line' as string]: 'rgba(36,30,23,0.14)',
    ['--accent' as string]: c.accent,
    ['--accent-soft' as string]: c.accentSoft,
    ['--gold' as string]: c.gold,
    ['--dark-bg' as string]: c.darkBg,
    fontFamily: "'EB Garamond',Georgia,serif",
    color: 'var(--ink)',
    background: 'var(--paper)',
    overflowX: 'hidden',
    WebkitFontSmoothing: 'antialiased',
  };

  return (
    <div ref={rootRef} className="cca-story" style={rootStyle}>
      <style>{SCOPED_CSS}</style>

      {/* Scroll-progress bar */}
      <div
        data-noprint
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          zIndex: 80,
          background: 'var(--paper)',
        }}
      >
        <div
          ref={progRef}
          style={{
            height: '100%',
            width: 0,
            background: 'var(--accent)',
            transition: 'width .12s linear',
          }}
        />
      </div>

      {/* Save-as-deck: prints the story as a landscape slide deck (one section per
          page — the print CSS in SCOPED_CSS freezes motion, keeps colours, hides
          chrome). Fixed so it's reachable while scrolling; hidden in the output
          itself via data-noprint. window.print() is called from the app's own
          bundle, so it's allowed under the page CSP (unlike a popup). */}
      <button
        data-noprint
        onClick={() => window.print()}
        title="Save the whole story as a landscape PDF slide deck (one slide per section)"
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 80,
          ...mono,
          fontSize: 11,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#fff',
          background: 'var(--accent)',
          border: 'none',
          borderRadius: 8,
          padding: '10px 16px',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.28)',
        }}
      >
        ↓ Save as deck
      </button>

      {/* ===== HERO ===== */}
      <section
        style={{
          position: 'relative',
          minHeight: '100vh',
          background: 'var(--dark-bg)',
          color: '#f3ece0',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '40px 0 52px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            ...container,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
            <img
              src={CREST}
              alt="Church of Confessing Anglicans"
              style={{
                width: 52,
                height: 52,
                display: 'block',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <div style={{ lineHeight: 1.4 }}>
              <div
                style={{
                  ...mono,
                  fontSize: 12,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: '#d6c8a6',
                }}
              >
                Church of Confessing Anglicans
              </div>
              <div
                style={{
                  ...mono,
                  fontSize: 11,
                  letterSpacing: '0.36em',
                  textTransform: 'uppercase',
                  color: 'var(--muted-on-dark)',
                }}
              >
                Aotearoa New Zealand
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <span
              style={{
                ...mono,
                fontSize: 11,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: 'var(--muted-on-dark)',
              }}
            >
              Diocese HQ - in numbers
            </span>
            {onSignIn && <CalcButton onClick={onSignIn} />}
          </div>
        </div>

        <div data-hero style={container}>
          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              ...mono,
              fontSize: 13,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: 'var(--gold)',
              marginBottom: 24,
            }}
          >
            What it costs to run our diocese
          </div>
          <div
            style={{
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(86px,15vw,236px)',
              lineHeight: 0.84,
              letterSpacing: '-0.02em',
              color: '#f3ece0',
            }}
          >
            $400,000
          </div>
          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              ...display,
              fontWeight: 500,
              fontSize: 'clamp(26px,4vw,48px)',
              color: '#cdbf9f',
              marginTop: 10,
              maxWidth: '20ch',
            }}
          >
            to run our diocese, every single year.
          </div>
        </div>

        <div
          data-noprint
          style={{
            ...container,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: 'var(--muted-on-dark)',
            ...mono,
            fontSize: 11,
            letterSpacing: '0.24em',
            textTransform: 'uppercase',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              fontSize: 15,
              animation: 'cca-scrollcue 1.8s ease-in-out infinite',
            }}
          >
            ↓
          </span>{' '}
          Scroll to see where it goes
        </div>
      </section>

      {/* ===== WHERE IT GOES ===== */}
      <section style={{ background: 'var(--paper)', padding: 'clamp(80px,12vh,158px) 0' }}>
        <div style={container}>
          <Kicker color="var(--accent)">Where it goes</Kicker>
          <h2
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: '0 0 14px',
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(34px,5.2vw,64px)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
              color: 'var(--ink)',
              maxWidth: '15ch',
            }}
          >
            Every dollar, accounted for.
          </h2>
          <p
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              maxWidth: '52ch',
              margin: '0 0 52px',
              fontSize: 'clamp(18px,1.5vw,22px)',
              lineHeight: 1.6,
              color: 'var(--ink-soft)',
            }}
          >
            The work of the diocese rests on four things. Here is the share each one takes of the
            $400,000 it costs us each year.
          </p>

          <SpendRow name="Personnel" amount="$220,000" pct="55%" fill={55} last={false}>
            Office of the Bishop and Office of the Registrar.
          </SpendRow>
          <SpendRow name="Operating" amount="$76,000" pct="19%" fill={19} delay={0.05} last={false}>
            Office expenses, Ordination &amp; discernment, and Training grants.
          </SpendRow>
          <SpendRow name="Travel" amount="$72,000" pct="18%" fill={18} delay={0.1} last={false}>
            Ministry carried across New Zealand (and around the world).
          </SpendRow>
          <SpendRow name="Governance" amount="$32,000" pct="8%" fill={8} delay={0.15} last>
            Clergy Conference, Clergy Days, Standing Committee, Synod, and Diocesan Trust Board.
          </SpendRow>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              marginTop: 50,
              background: 'var(--paper-2)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              padding: 'clamp(24px,3vw,38px)',
            }}
          >
            <div
              style={{
                ...mono,
                fontSize: 12,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: 12,
              }}
            >
              What this does not include
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 'clamp(18px,1.6vw,23px)',
                lineHeight: 1.55,
                color: 'var(--ink)',
              }}
            >
              This is the cost of running the diocese only. It{' '}
              <strong
                style={{ color: 'var(--ink)', textDecoration: 'underline', textUnderlineOffset: 3 }}
              >
                does not include
              </strong>{' '}
              <strong style={{ color: 'var(--ink)' }}>church plants</strong> or{' '}
              <strong style={{ color: 'var(--ink)' }}>theological education</strong>. Those are
              funded separately, from outside sources, and sit beyond this $400,000.
            </p>
          </div>
        </div>
      </section>

      {/* ===== WHERE THE MONEY COMES FROM ===== */}
      <section
        style={{
          background: 'var(--paper)',
          padding: 'clamp(80px,12vh,158px) 0',
          borderTop: '1px solid var(--line)',
        }}
      >
        <div style={container}>
          <Kicker color="var(--accent)">Where the money comes from</Kicker>
          <h2
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: '0 0 16px',
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(34px,5.2vw,64px)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
              color: 'var(--ink)',
              maxWidth: '18ch',
            }}
          >
            Our churches give what they can. It is not yet enough.
          </h2>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
              gap: 28,
              margin: '48px 0 58px',
            }}
          >
            <Stat n="18">churches in total</Stat>
            <Stat n="12">
              parishes &amp; provisional parishes that{' '}
              <span style={{ color: 'var(--ink)' }}>must</span> contribute
            </Stat>
            <Stat n="6">
              branch churches that are <span style={{ color: 'var(--ink)' }}>encouraged</span> to
              contribute
            </Stat>
          </div>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              background: 'var(--paper-2)',
              border: '1px solid var(--line)',
              borderRadius: 18,
              padding: 'clamp(26px,3.4vw,44px)',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                gap: 20,
                marginBottom: 26,
              }}
            >
              <div>
                <div
                  style={{
                    ...mono,
                    fontSize: 12,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                    marginBottom: 4,
                  }}
                >
                  What we need
                </div>
                <div
                  style={{
                    ...display,
                    fontWeight: 600,
                    fontSize: 'clamp(40px,5.5vw,68px)',
                    lineHeight: 0.9,
                    color: 'var(--ink)',
                  }}
                >
                  $400,000
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div
                  style={{
                    ...mono,
                    fontSize: 12,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                    marginBottom: 4,
                  }}
                >
                  What we receive
                </div>
                <div
                  style={{
                    ...display,
                    fontWeight: 600,
                    fontSize: 'clamp(40px,5.5vw,68px)',
                    lineHeight: 0.9,
                    color: 'var(--ink)',
                  }}
                >
                  $250,000
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                height: 56,
                borderRadius: 11,
                overflow: 'hidden',
                background: 'rgba(36,30,23,0.06)',
              }}
            >
              <div
                data-fill="62.5"
                style={{
                  width: 0,
                  background: 'var(--ink)',
                  transition: `width 1.3s ${EASE}`,
                  ...segLabel,
                  color: '#f3ece0',
                }}
              >
                $250,000 given
              </div>
              <div
                data-fill="37.5"
                style={{
                  width: 0,
                  background: 'var(--accent)',
                  transition: `width 1.3s ${EASE} .12s`,
                  ...segLabel,
                  color: '#fff',
                }}
              >
                $150,000 short
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 14,
                ...mono,
                fontSize: 12,
                letterSpacing: '0.04em',
                color: 'var(--muted)',
              }}
            >
              <span>Given by our churches</span>
              <span style={{ color: 'var(--accent)' }}>The shortfall</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== THE GAP / RESERVES ===== */}
      <section
        style={{
          background: 'var(--dark-bg)',
          color: '#f3ece0',
          padding: 'clamp(90px,14vh,182px) 0',
        }}
      >
        <div style={container}>
          <Kicker color="var(--gold)">The gap, today</Kicker>
          <div
            data-count="150000"
            data-parallax="0.03"
            data-prefix="$"
            style={{
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(66px,12vw,180px)',
              lineHeight: 0.86,
              letterSpacing: '-0.02em',
              color: '#f3ece0',
            }}
          >
            $150,000
          </div>
          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              ...display,
              fontWeight: 500,
              fontSize: 'clamp(26px,4vw,46px)',
              color: '#cdbf9f',
              marginTop: 10,
              maxWidth: '20ch',
            }}
          >
            met each year from external contributors and our savings, not from income.
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
              gap: 'clamp(28px,4vw,64px)',
              marginTop: 'clamp(48px,7vh,84px)',
              maxWidth: 920,
            }}
          >
            <p
              data-reveal
              style={{
                opacity: 1,
                transition: REVEAL_TRANSITION,
                margin: 0,
                fontSize: 'clamp(18px,1.5vw,22px)',
                lineHeight: 1.62,
                color: '#d6cdbd',
              }}
            >
              Those reserves were raised from outside, e.g.{' '}
              <strong style={{ color: '#f3ece0', fontWeight: 600 }}>$300,000</strong> to put our
              Bishop into full-time ministry in 2022, and the balance from generous overseas
              partners who have stood with us since we began.
            </p>
            <p
              data-reveal
              style={{
                opacity: 1,
                transition: REVEAL_TRANSITION,
                margin: 0,
                fontSize: 'clamp(18px,1.5vw,22px)',
                lineHeight: 1.62,
                color: '#d6cdbd',
              }}
            >
              This money is a mix of savings and gifts from outside the diocese. We cannot continue
              to rely on it, and what we have is running down. We must learn to make ends meet from
              what our own churches give.
            </p>
          </div>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              marginTop: 'clamp(44px,6vh,72px)',
              maxWidth: 680,
            }}
          >
            <div
              style={{
                ...mono,
                fontSize: 12,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'var(--muted-on-dark)',
              }}
            >
              Reserves remaining
            </div>
            <div
              style={{
                height: 14,
                borderRadius: 999,
                background: 'rgba(243,236,224,0.12)',
                overflow: 'hidden',
                marginTop: 12,
              }}
            >
              <div
                data-fill="26"
                data-start="100"
                style={{
                  height: '100%',
                  width: '100%',
                  background: 'var(--accent)',
                  transition: 'width 1.9s cubic-bezier(.4,0,.2,1)',
                }}
              />
            </div>
            <div
              style={{
                ...mono,
                fontSize: 12,
                letterSpacing: '0.04em',
                color: 'var(--muted-on-dark)',
                marginTop: 12,
              }}
            >
              Drawn down, year after year
            </div>
          </div>
        </div>
      </section>

      {/* ===== CONCENTRATION ===== */}
      <section style={{ background: 'var(--paper)', padding: 'clamp(80px,12vh,158px) 0' }}>
        <div style={container}>
          <Kicker color="var(--accent)">A heavy reliance on a few</Kicker>
          <h2
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: '0 0 14px',
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(34px,5.2vw,64px)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
              color: 'var(--ink)',
              maxWidth: '18ch',
            }}
          >
            Two-thirds of our funding rests on just four congregations.
          </h2>
          <p
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              maxWidth: '52ch',
              margin: '0 0 8px',
              fontSize: 'clamp(18px,1.5vw,22px)',
              lineHeight: 1.6,
              color: 'var(--ink-soft)',
            }}
          >
            Of the $250,000 our churches give, most of it comes from a small handful.
          </p>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 30,
              flexWrap: 'wrap',
              margin: '38px 0 30px',
            }}
          >
            <div
              data-count="65"
              data-parallax="0.03"
              data-suffix="%"
              style={{
                ...display,
                fontWeight: 600,
                fontSize: 'clamp(66px,11vw,154px)',
                lineHeight: 0.82,
                color: 'var(--accent)',
              }}
            >
              65%
            </div>
            <div
              style={{
                fontFamily: "'EB Garamond',serif",
                fontSize: 'clamp(18px,1.6vw,23px)',
                color: 'var(--ink-soft)',
                maxWidth: '22ch',
                paddingBottom: 12,
                lineHeight: 1.5,
              }}
            >
              of all church giving comes from the four largest churches.
            </div>
          </div>

          <div data-reveal style={{ opacity: 1, transition: REVEAL_TRANSITION, marginTop: 18 }}>
            <div style={{ display: 'flex', height: 50, borderRadius: 10, overflow: 'hidden' }}>
              <div
                data-fill="65"
                style={{
                  width: 0,
                  background: 'var(--accent)',
                  transition: `width 1.3s ${EASE}`,
                  ...segLabel,
                  paddingLeft: 16,
                  color: '#fff',
                }}
              >
                Four largest churches · 65%
              </div>
              <div
                data-fill="35"
                style={{
                  width: 0,
                  background: 'rgba(36,30,23,0.16)',
                  transition: `width 1.3s ${EASE} .1s`,
                  ...segLabel,
                  paddingLeft: 16,
                  color: 'var(--ink)',
                }}
              >
                All other churches · 35%
              </div>
            </div>
          </div>

          <p
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: 'clamp(40px,6vh,64px) 0 0',
              ...display,
              fontWeight: 500,
              fontSize: 'clamp(22px,2.6vw,34px)',
              lineHeight: 1.3,
              color: 'var(--ink)',
              maxWidth: '28ch',
            }}
          >
            If even one of these four churches had a change in its giving, the financial effect on
            the diocese would be <span style={{ color: 'var(--accent)' }}>severe</span>.
          </p>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              marginTop: 46,
              background: 'var(--paper-2)',
              border: '1px solid var(--line)',
              borderRadius: 16,
              padding: 'clamp(24px,3vw,38px)',
            }}
          >
            <div
              style={{
                ...mono,
                fontSize: 12,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                marginBottom: 12,
              }}
            >
              Why this matters
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 'clamp(18px,1.6vw,22px)',
                lineHeight: 1.55,
                color: 'var(--ink)',
              }}
            >
              A funding base this concentrated leaves the whole diocese exposed to the circumstances
              of a very small number of churches. A healthy diocese needs a wider, steadier
              foundation, so that no single congregation carries a burden it cannot sustain.
            </p>
          </div>
        </div>
      </section>

      {/* ===== WHY NOT RAISE THE RATE ===== */}
      <section
        style={{
          background: 'var(--paper-2)',
          padding: 'clamp(80px,12vh,158px) 0',
          borderTop: '1px solid var(--line)',
        }}
      >
        <div style={container}>
          <Kicker color="var(--accent)">Why we cannot simply raise the rate</Kicker>
          <h2
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: '0 0 36px',
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(34px,5.2vw,64px)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
              color: 'var(--ink)',
              maxWidth: '16ch',
            }}
          >
            The quota has not moved since it began.
          </h2>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))',
              gap: 24,
              marginBottom: 'clamp(48px,7vh,80px)',
            }}
          >
            <Principle big="6.5%">The rate since the quota began, never once increased.</Principle>
            <Principle big="Equal">
              Every parish is levied at the same proportional rate, by Canon.
            </Principle>
            <Principle big="Buildings">
              The only income excluded is money raised for buildings.
            </Principle>
          </div>

          <h3
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: '0 0 28px',
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(26px,3.6vw,44px)',
              lineHeight: 1.08,
              color: 'var(--ink)',
              maxWidth: '22ch',
            }}
          >
            A flat increase may stifle the very ministry we exist to protect.
          </h3>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(18px,3vw,40px)',
              flexWrap: 'wrap',
              marginBottom: 36,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                style={{
                  ...display,
                  fontWeight: 600,
                  fontSize: 'clamp(48px,7vw,92px)',
                  lineHeight: 0.85,
                  color: 'var(--ink)',
                }}
              >
                6.5%
              </div>
              <div
                style={{
                  ...mono,
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                  marginTop: 8,
                }}
              >
                today
              </div>
            </div>
            <div
              style={{
                ...display,
                fontSize: 'clamp(36px,5vw,64px)',
                color: 'var(--muted)',
                lineHeight: 0.85,
              }}
            >
              →
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                style={{
                  ...display,
                  fontWeight: 600,
                  fontSize: 'clamp(48px,7vw,92px)',
                  lineHeight: 0.85,
                  color: 'var(--accent)',
                }}
              >
                10.5%
              </div>
              <div
                style={{
                  ...mono,
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: 'var(--accent)',
                  marginTop: 8,
                }}
              >
                the flat rate needed
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
              gap: 'clamp(28px,4vw,56px)',
              maxWidth: 980,
            }}
          >
            <div data-reveal style={{ opacity: 1, transition: REVEAL_TRANSITION }}>
              <p
                style={{
                  margin: '0 0 18px',
                  fontSize: 'clamp(18px,1.5vw,22px)',
                  lineHeight: 1.6,
                  color: 'var(--ink-soft)',
                }}
              >
                Because the Canons require every parish to be levied at the same proportional rate,
                we cannot solve this church by church. Simply lifting the quota from 6.5% to 10.5%
                will not work.
              </p>
              <p
                style={{
                  margin: '0 0 18px',
                  fontSize: 'clamp(18px,1.5vw,22px)',
                  lineHeight: 1.6,
                  color: 'var(--ink-soft)',
                }}
              >
                For some churches it would be unsustainable outright. For our largest contributors
                it would force the very cuts we least want to see.
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: 'clamp(18px,1.5vw,22px)',
                  lineHeight: 1.6,
                  color: 'var(--ink-soft)',
                }}
              >
                A move away from flat rates, or standardised proportions, may help.
              </p>
            </div>
            <div data-reveal style={{ opacity: 1, transition: REVEAL_TRANSITION }}>
              <div
                style={{
                  ...mono,
                  fontSize: 12,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'var(--accent)',
                  marginBottom: 16,
                }}
              >
                What a flat increase would cost
              </div>
              <CostItem first>Fewer local church ministers and staff</CostItem>
              <CostItem>Less ministry in the local church</CostItem>
              <CostItem last>Fewer ministry apprenticeships and trainees</CostItem>
            </div>
          </div>

          <p
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: '38px 0 0',
              ...display,
              fontWeight: 500,
              fontSize: 'clamp(20px,2.4vw,30px)',
              lineHeight: 1.32,
              color: 'var(--ink)',
              maxWidth: '34ch',
            }}
          >
            That is the local mission and ministry the diocese exists to serve, not to diminish. So
            we must think carefully.
          </p>
        </div>
      </section>

      {/* ===== CLOSING ===== */}
      <section
        style={{
          background: 'var(--dark-bg)',
          color: '#f3ece0',
          padding: 'clamp(100px,16vh,200px) 0',
          minHeight: '88vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={container}>
          <Kicker color="var(--gold)">The work before us</Kicker>
          <h2
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: '0 0 clamp(48px,7vh,80px)',
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(34px,5.6vw,72px)',
              lineHeight: 1,
              letterSpacing: '-0.01em',
              color: '#f3ece0',
              maxWidth: '18ch',
            }}
          >
            Whatever we design must do two things at once.
          </h2>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))',
              gap: 'clamp(32px,5vw,72px)',
            }}
          >
            <Pillar num="01" title="A self-sustaining diocese">
              Raise enough, reliably, that we no longer depend on savings that will not be
              replenished.
            </Pillar>
            <Pillar num="02" title="Local ministry protected" delay={0.12}>
              Protect each church&apos;s ability to staff, train and send people for the work of the
              gospel.
            </Pillar>
          </div>

          <p
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              margin: 'clamp(56px,8vh,96px) 0 0',
              ...display,
              fontWeight: 500,
              fontStyle: 'italic',
              fontSize: 'clamp(24px,3.4vw,46px)',
              lineHeight: 1.25,
              color: '#cdbf9f',
              maxWidth: '24ch',
            }}
          >
            Two things, held together. That is the work before us.
          </p>

          <div
            data-reveal
            style={{
              opacity: 1,
              transition: REVEAL_TRANSITION,
              marginTop: 'clamp(48px,7vh,80px)',
              paddingTop: 24,
              borderTop: '1px solid rgba(243,236,224,0.14)',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <img
              src={CREST}
              alt="Church of Confessing Anglicans"
              style={{
                width: 40,
                height: 40,
                display: 'block',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                ...mono,
                fontSize: 11,
                letterSpacing: '0.24em',
                textTransform: 'uppercase',
                color: 'var(--muted-on-dark)',
              }}
            >
              Church of Confessing Anglicans Aotearoa New Zealand
            </span>
            {onSignIn && (
              <div style={{ marginLeft: 'auto' }}>
                <CalcButton onClick={onSignIn} />
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Shared style fragments ──────────────────────────────────────────────────
const container: CSSProperties = {
  maxWidth: 1140,
  margin: '0 auto',
  padding: '0 38px',
  width: '100%',
};
const mono: CSSProperties = { fontFamily: "'IBM Plex Mono',monospace" };
const display: CSSProperties = { fontFamily: "'Cormorant Garamond',serif" };
const segLabel: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 18,
  ...mono,
  fontSize: 13,
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};
const calcBtn: CSSProperties = {
  ...mono,
  fontSize: 11,
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  color: 'var(--gold)',
  background: 'transparent',
  border: '1px solid rgba(169,128,63,0.4)',
  borderRadius: 6,
  padding: '7px 14px',
  cursor: 'pointer',
};

// Quiet entry into the OTP flow → Explorer (the calculator). Same button in the
// hero and at the foot of the closing section.
function CalcButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button data-noprint onClick={onClick} style={calcBtn}>
      Calculator
    </button>
  );
}

// ── Small presentational helpers ────────────────────────────────────────────
function Kicker({ children, color }: { children: ReactNode; color: string }): JSX.Element {
  return (
    <div
      data-reveal
      style={{
        opacity: 1,
        transition: REVEAL_TRANSITION,
        ...mono,
        fontSize: 13,
        letterSpacing: '0.26em',
        textTransform: 'uppercase',
        color,
        marginBottom: 18,
      }}
    >
      {children}
    </div>
  );
}

function SpendRow({
  name,
  amount,
  pct,
  fill,
  children,
  delay = 0,
  last,
}: {
  name: string;
  amount: string;
  pct: string;
  fill: number;
  children: ReactNode;
  delay?: number;
  last: boolean;
}): JSX.Element {
  return (
    <div
      data-reveal
      style={{
        opacity: 1,
        transition: REVEAL_TRANSITION,
        display: 'grid',
        gap: 13,
        padding: '26px 0',
        borderTop: '1px solid var(--line)',
        ...(last ? { borderBottom: '1px solid var(--line)' } : {}),
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 20,
        }}
      >
        <div
          style={{
            ...display,
            fontWeight: 600,
            fontSize: 'clamp(24px,2.8vw,34px)',
            color: 'var(--ink)',
          }}
        >
          {name}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 22 }}>
          <div style={{ ...mono, fontSize: 14, color: 'var(--muted)' }}>{amount}</div>
          <div
            style={{
              ...display,
              fontWeight: 600,
              fontSize: 'clamp(30px,3.4vw,46px)',
              color: 'var(--ink)',
              minWidth: '2.8ch',
              textAlign: 'right',
            }}
          >
            {pct}
          </div>
        </div>
      </div>
      <div
        style={{
          height: 9,
          borderRadius: 999,
          background: 'rgba(36,30,23,0.08)',
          overflow: 'hidden',
        }}
      >
        <div
          data-fill={String(fill)}
          style={{
            height: '100%',
            width: 0,
            borderRadius: 999,
            background: 'var(--ink)',
            transition: `width 1.2s ${EASE}${delay ? ` ${delay}s` : ''}`,
          }}
        />
      </div>
      <div style={{ fontSize: 'clamp(15px,1.3vw,18px)', color: 'var(--ink-soft)' }}>{children}</div>
    </div>
  );
}

function Stat({ n, children }: { n: string; children: ReactNode }): JSX.Element {
  return (
    <div style={{ borderTop: '2px solid var(--ink)', paddingTop: 16 }}>
      <div
        style={{
          ...display,
          fontWeight: 600,
          fontSize: 'clamp(48px,7vw,86px)',
          lineHeight: 0.9,
          color: 'var(--ink)',
        }}
      >
        {n}
      </div>
      <div
        style={{
          ...mono,
          fontSize: 12,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginTop: 12,
          lineHeight: 1.6,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Principle({ big, children }: { big: string; children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: 26,
      }}
    >
      <div
        style={{
          ...display,
          fontWeight: 600,
          fontSize: 'clamp(38px,4vw,54px)',
          lineHeight: 0.9,
          color: 'var(--ink)',
          marginBottom: 14,
        }}
      >
        {big}
      </div>
      <div
        style={{ fontSize: 'clamp(16px,1.4vw,19px)', lineHeight: 1.5, color: 'var(--ink-soft)' }}
      >
        {children}
      </div>
    </div>
  );
}

function CostItem({
  children,
  first,
  last,
}: {
  children: ReactNode;
  first?: boolean;
  last?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 14,
        padding: '14px 0',
        borderTop: '1px solid var(--line)',
        ...(last ? { borderBottom: '1px solid var(--line)' } : {}),
        ...(first ? {} : {}),
      }}
    >
      <span style={{ color: 'var(--accent)', ...display, fontSize: 22, lineHeight: 1 }}>•</span>
      <span style={{ fontSize: 'clamp(17px,1.4vw,20px)', lineHeight: 1.45, color: 'var(--ink)' }}>
        {children}
      </span>
    </div>
  );
}

function Pillar({
  num,
  title,
  children,
  delay = 0,
}: {
  num: string;
  title: string;
  children: ReactNode;
  delay?: number;
}): JSX.Element {
  return (
    <div
      data-reveal
      style={{
        opacity: 1,
        transition: delay
          ? `opacity .9s ${EASE} ${delay}s,transform .9s ${EASE} ${delay}s`
          : REVEAL_TRANSITION,
        borderTop: '2px solid var(--gold)',
        paddingTop: 22,
      }}
    >
      <div
        style={{
          ...mono,
          fontSize: 13,
          letterSpacing: '0.2em',
          color: 'var(--gold)',
          marginBottom: 14,
        }}
      >
        {num}
      </div>
      <h3
        style={{
          margin: '0 0 14px',
          ...display,
          fontWeight: 600,
          fontSize: 'clamp(26px,3.2vw,40px)',
          lineHeight: 1.05,
          color: '#f3ece0',
        }}
      >
        {title}
      </h3>
      <p
        style={{ margin: 0, fontSize: 'clamp(17px,1.5vw,21px)', lineHeight: 1.6, color: '#d6cdbd' }}
      >
        {children}
      </p>
    </div>
  );
}
